import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { CocktailWithRelations } from '@/features/cocktails/types';
import type { PublicInventoryItem } from '@/features/tasting/useTastingItems';
import type { PublicCategoryType } from '@/features/inventory/types';
import { inferWineColor, type WineColor } from '@/features/tasting/wineColor';

const KEY = ['top-rated'] as const;

// Les top-rated changent rarement (nouvelle note client = quelques fois /
// jour). 30 min de cache évite des refetch à chaque navigation entre Home,
// /cocktails, /degustation, /vins. Le user voit toujours du frais à l'ouverture
// de la session, et React Query rafraîchit en arrière-plan au prochain focus.
const TOP_RATED_STALE_MS = 30 * 60 * 1000;

export interface TopCocktail {
  cocktail: CocktailWithRelations;
  rating_avg: number;
  rating_count: number;
}

export interface TopTastingItem {
  item: PublicInventoryItem;
  rating_avg: number;
  rating_count: number;
}

export interface TopWineByColor {
  color: WineColor;
  item: PublicInventoryItem;
  rating_avg: number;
  rating_count: number;
}

/**
 * Top N cocktails publiés par note moyenne descendante.
 * Minimum 1 avis pour apparaître (sinon tout cocktail publié sans
 * note pollue le top). Trie d'abord par avg, puis par count.
 */
export function useTopRatedCocktails(limit: number = 3) {
  return useQuery({
    queryKey: [...KEY, 'cocktails', limit],
    staleTime: TOP_RATED_STALE_MS,
    queryFn: async (): Promise<TopCocktail[]> => {
      // 1. Summaries pour 'cocktail'
      const { data: summaries, error: sErr } = await supabase
        .from('customer_ratings_summary')
        .select('*')
        .eq('item_type', 'cocktail')
        .gte('rating_count', 1)
        .order('rating_avg', { ascending: false })
        .order('rating_count', { ascending: false })
        .limit(limit * 2); // safety pour absorber les non-publics
      if (sErr) throw sErr;
      if (!summaries || summaries.length === 0) return [];

      // 2. Charge les cocktails (seulement is_public=true)
      const ids = summaries.map((s: any) => s.item_id);
      const { data: cocktails, error: cErr } = await supabase
        .from('cocktails')
        .select(`*, category:cocktail_categories(id, label)`)
        .in('id', ids)
        .eq('is_public', true);
      if (cErr) throw cErr;

      // Filtre défensif : rejette les lignes sans id/name ou avec une
      // category cassée. Évite que la card du TopRatedCarousel crash si
      // un cocktail a été dépublié juste après que les summaries soient
      // chargées.
      const cleanedCocktails = (
        (cocktails ?? []) as Array<Record<string, unknown>>
      ).filter(
        (c) =>
          typeof c.id === 'string' &&
          typeof c.name === 'string' &&
          (c.category == null || typeof c.category === 'object')
      ) as unknown as CocktailWithRelations[];

      const cocktailMap = new Map<string, CocktailWithRelations>();
      for (const c of cleanedCocktails) {
        cocktailMap.set(c.id, c);
      }

      // 3. Combine en respectant l'ordre des summaries
      const result: TopCocktail[] = [];
      for (const s of summaries as Array<{
        item_id: string;
        rating_avg: number;
        rating_count: number;
      }>) {
        const c = cocktailMap.get(s.item_id);
        if (c) {
          result.push({
            cocktail: c,
            rating_avg: s.rating_avg,
            rating_count: s.rating_count,
          });
        }
        if (result.length >= limit) break;
      }
      return result;
    },
  });
}

/**
 * Top N tasting items publiés (whisky/gin/beer/wine/spirit/etc) par
 * note moyenne descendante. Filtre par item_type IN itemTypes.
 *
 * Utilisé par TopRatedTastingCarousel sur /degustation et /vins.
 * Min 1 avis requis. Trie par avg desc puis count desc.
 */
export function useTopRatedTastingItems(
  itemTypes: PublicCategoryType[],
  limit: number = 6
) {
  // Sérialise itemTypes pour stabilité de la queryKey.
  const typesKey = [...itemTypes].sort().join(',');
  return useQuery({
    queryKey: [...KEY, 'tasting', typesKey, limit],
    enabled: itemTypes.length > 0,
    staleTime: TOP_RATED_STALE_MS,
    queryFn: async (): Promise<TopTastingItem[]> => {
      // 1. Summaries filtré par item_type
      const { data: summaries, error: sErr } = await supabase
        .from('customer_ratings_summary')
        .select('*')
        .in('item_type', itemTypes)
        .gte('rating_count', 1)
        .order('rating_avg', { ascending: false })
        .order('rating_count', { ascending: false })
        .limit(limit * 2);
      if (sErr) throw sErr;
      if (!summaries || summaries.length === 0) return [];

      // 2. Charge les items via la vue publique (filtre is_public côté DB)
      const ids = summaries.map((s: any) => s.item_id);
      const { data: items, error: iErr } = await supabase
        .from('public_inventory_items')
        .select('*')
        .in('id', ids);
      if (iErr) throw iErr;

      // Filtre défensif : rejette les items dépubliés ou cassés
      const cleaned = (
        (items ?? []) as Array<Record<string, unknown>>
      ).filter(
        (it) =>
          typeof it.id === 'string' &&
          typeof it.name === 'string' &&
          typeof it.category_id === 'string'
      ) as unknown as PublicInventoryItem[];

      const itemMap = new Map<string, PublicInventoryItem>();
      for (const it of cleaned) {
        itemMap.set(it.id, it);
      }

      // 3. Combine en respectant l'ordre des summaries
      const result: TopTastingItem[] = [];
      for (const s of summaries as Array<{
        item_id: string;
        rating_avg: number;
        rating_count: number;
      }>) {
        const it = itemMap.get(s.item_id);
        if (it) {
          result.push({
            item: it,
            rating_avg: s.rating_avg,
            rating_count: s.rating_count,
          });
        }
        if (result.length >= limit) break;
      }
      return result;
    },
  });
}

/**
 * Top 1 vin par couleur (rouge, blanc, rosé, bulles). Utilisé par
 * /vins pour afficher un carousel "Le meilleur de chaque couleur".
 *
 * Détecte la couleur via inferWineColor() sur le nom de l'item. Les
 * vins dont la couleur est indéterminée sont ignorés. Si aucune note
 * pour une couleur, la couleur est absente du résultat.
 */
export function useTopRatedWinesByColor() {
  return useQuery({
    queryKey: [...KEY, 'wines-by-color'],
    staleTime: TOP_RATED_STALE_MS,
    queryFn: async (): Promise<TopWineByColor[]> => {
      // 1. Toutes les summaries pour item_type='wine', triées par avg desc
      const { data: summaries, error: sErr } = await supabase
        .from('customer_ratings_summary')
        .select('*')
        .eq('item_type', 'wine')
        .gte('rating_count', 1)
        .order('rating_avg', { ascending: false })
        .order('rating_count', { ascending: false });
      if (sErr) throw sErr;
      if (!summaries || summaries.length === 0) return [];

      // 2. Charge tous les vins concernés
      const ids = summaries.map((s: any) => s.item_id);
      const { data: items, error: iErr } = await supabase
        .from('public_inventory_items')
        .select('*')
        .in('id', ids);
      if (iErr) throw iErr;

      const itemMap = new Map<string, PublicInventoryItem>();
      for (const it of (items ?? []) as PublicInventoryItem[]) {
        if (
          typeof it.id === 'string' &&
          typeof it.name === 'string' &&
          typeof it.category_id === 'string'
        ) {
          itemMap.set(it.id, it);
        }
      }

      // 3. Première occurrence de chaque couleur (déjà trié par avg desc)
      const colorsSeen = new Set<WineColor>();
      const result: TopWineByColor[] = [];
      for (const s of summaries as Array<{
        item_id: string;
        rating_avg: number;
        rating_count: number;
      }>) {
        const it = itemMap.get(s.item_id);
        if (!it) continue;
        const color = inferWineColor(it.name, it.category_label);
        if (!color || colorsSeen.has(color)) continue;
        colorsSeen.add(color);
        result.push({
          color,
          item: it,
          rating_avg: s.rating_avg,
          rating_count: s.rating_count,
        });
        // Ordre d'affichage : rouge, blanc, rosé, bulles
        if (colorsSeen.size >= 4) break;
      }

      // Tri final dans l'ordre canonique (red → white → rose → sparkling)
      const order: WineColor[] = ['red', 'white', 'rose', 'sparkling'];
      result.sort(
        (a, b) => order.indexOf(a.color) - order.indexOf(b.color)
      );
      return result;
    },
  });
}
